import { useCallback, useEffect, useRef, useState } from "react";
import type { ContentBlock, EditorComment, IssueArticle, IssueDetail, IssueEditable, IssueVersion, NewsletterSection } from "./segment-types";
import { DEMO_MODE } from "./demo-data";
import { IssueSync, mergeIssues, type SyncState } from "./issue-sync";
import { defaultSections } from "./sections";
import { DEFAULT_UTM, utmCampaignFromTitle } from "./utm";

const STORAGE_KEY = "nl-issues-v1";
export const CURRENT_AUTHOR = "Sara J.";

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `i-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function blockHasContent(block: ContentBlock): boolean {
  if (block.type === "divider" || block.type === "footer") return true;
  if (block.type === "image" || block.type === "link" || block.type === "article" || block.type === "cta" || block.type === "hero") {
    return !!(block.url && block.url.trim()) || !!(block.text && block.text.trim());
  }
  return !!(block.text && block.text.trim());
}

/** A section counts as content once it would render something to a reader. */
function sectionHasContent(x: NewsletterSection): boolean {
  if (x.type === "divider" || x.type === "spacer" || x.type === "footer") return false;
  // A category-driven slot fills itself at send time, so it is content even
  // while it is empty here.
  if (x.type === "featured" || x.type === "articles" || x.type === "category") return true;
  return Boolean((x.text ?? "").trim() || (x.image ?? "").trim() || (x.url ?? "").trim());
}

export function deriveNextAction(issue: Pick<IssueDetail, "status" | "title" | "subject" | "articles_list" | "blocks" | "sections">): string | undefined {
  if (issue.status === "sent" || issue.status === "scheduled") return undefined;
  if (issue.status === "approved") return "Send from Broadcasts";
  if (issue.status === "in_review") return "Approve or request changes";
  if (!issue.title.trim()) return "Add a title";
  if (!issue.subject.trim()) return "Add a subject line";
  const hasBlocks = (issue.blocks ?? []).some(blockHasContent)
    || (issue.sections ?? []).some(sectionHasContent);
  const n = issue.articles_list?.length ?? 0;
  if (!hasBlocks && n < 1) return "Add photos, text, links, or articles";
  return "Submit for review";
}

function withDerived(issue: IssueDetail): IssueDetail {
  const list = issue.articles_list ?? [];
  const blocks = issue.blocks ?? [];
  return {
    ...issue,
    articles: list.length,
    articles_list: list,
    blocks,
    sender_name: issue.sender_name || "Altibbi",
    reply_to: issue.reply_to || "newsletter@altibbi.me",
    utm: {
      ...DEFAULT_UTM,
      ...issue.utm,
      source: issue.utm?.source || DEFAULT_UTM.source,
      medium: issue.utm?.medium || DEFAULT_UTM.medium,
      campaign: issue.utm?.campaign || utmCampaignFromTitle(issue.title),
    },
    comments: issue.comments ?? [],
    versions: issue.versions ?? [],
    next_action: deriveNextAction({ ...issue, articles_list: list, blocks, sections: issue.sections }),
  };
}

/**
 * `null` means nothing has been stored yet and the API should seed us. An
 * empty array means the workspace genuinely has no newsletters.
 *
 * Treating `[]` as "nothing stored" made an empty workspace re-seed from the
 * API on every mount and write `[]` back — which clobbered a draft another
 * page had just created.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readStored(): IssueDetail[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    let rows = parsed as IssueDetail[];
    // Sample newsletters (ids "i1", "i2" …) got into browsers on the live site
    // when a failed request fell back to demo data, and autosave then sent
    // them to the server as real ones. A real newsletter always has a UUID.
    if (!DEMO_MODE) rows = rows.filter((r) => UUID.test(String(r?.id ?? "")));
    return rows.map((row) => withDerived(row));
  } catch {
    return null;
  }
}

function writeStored(issues: IssueDetail[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(issues));
}

export function canEditIssue(issue: IssueDetail): boolean {
  return issue.status === "draft";
}

export function canSubmit(issue: IssueDetail): boolean {
  const hasBlocks = (issue.blocks ?? []).some(blockHasContent)
    || (issue.sections ?? []).some(sectionHasContent);
  const hasArticles = (issue.articles_list?.length ?? 0) > 0;
  return (
    issue.status === "draft" &&
    issue.title.trim().length > 0 &&
    issue.subject.trim().length > 0 &&
    (hasBlocks || hasArticles)
  );
}

function snapshotVersion(issue: IssueDetail, actor: string): IssueVersion {
  const versions = issue.versions ?? [];
  return {
    id: newId(),
    n: versions.length + 1,
    status: issue.status,
    at: new Date().toISOString(),
    actor,
    title: issue.title,
    subject: issue.subject,
    blocks: JSON.parse(JSON.stringify(issue.blocks ?? [])) as ContentBlock[],
    // Without these a restored version carried an old title over today's
    // structure — a newsletter that never existed.
    sections: JSON.parse(JSON.stringify(issue.sections ?? [])) as NewsletterSection[],
    categories: [...(issue.categories ?? [])],
    notes: issue.notes,
    preheader: issue.preheader,
    utm: issue.utm,
  };
}

/**
 * The house format, so a new newsletter opens as a newsletter.
 *
 * It used to open with an empty heading block and an empty text block left
 * over from the generic block model. Both converted to unnamed "Text"
 * sections, so the first thing anyone saw on a new draft was two identical
 * cards marked "Needs setup" and no hint of what a newsletter here looks
 * like. The standard shape is the product decision — start from it.
 */
function starterSections(): NewsletterSection[] {
  return defaultSections();
}

export function createDraft(input: { title: string; subject: string; notes?: string }): IssueDetail {
  const now = new Date().toISOString();
  return withDerived({
    id: newId(),
    title: input.title.trim(),
    subject: input.subject.trim(),
    status: "draft",
    updated_at: now,
    author: CURRENT_AUTHOR,
    articles: 0,
    open_rate: null,
    articles_list: [],
    blocks: [],
    sections: starterSections(),
    categories: [],
    notes: input.notes?.trim() || "Draft — design the email, then submit for editorial review.",
    preheader: "",
    sender_name: "Altibbi",
    reply_to: "newsletter@altibbi.me",
    utm: { ...DEFAULT_UTM, campaign: utmCampaignFromTitle(input.title.trim() || "newsletter") },
    reviewer: null,
    comments: [],
    versions: [],
  });
}

export function useIssueStore(seed: IssueDetail[] | undefined) {
  const [issues, setIssues] = useState<IssueDetail[] | null>(() => readStored());
  const [sync, setSync] = useState<SyncState>({ status: "idle" });
  // The server copy is what makes a newsletter visible to anyone else. Demo
  // mode has no server to write to, and the API refuses writes there rather
  // than reporting a success it did not perform.
  const syncRef = useRef<IssueSync | null>(null);
  if (syncRef.current === null) syncRef.current = new IssueSync(setSync, DEMO_MODE);
  useEffect(() => () => syncRef.current?.dispose(), []);
  // React may drop a pending update if the component unmounts first — which
  // is exactly what happens when a page creates a draft and navigates to it
  // in the same handler. Writes go through this ref so they do not depend on
  // an updater ever running.
  const issuesRef = useRef<IssueDetail[] | null>(issues);
  issuesRef.current = issues;

  const commit = useCallback((next: IssueDetail[]) => {
    issuesRef.current = next;
    // Local first, and synchronously: undo, version history and the preview
    // all read a value that is already there. The server catches up after.
    writeStored(next);
    setIssues(next);
    syncRef.current?.schedule(next);
  }, []);

  // Merge the server's newsletters in once, the first time they arrive. This
  // used to be skipped whenever local storage held anything, so a browser that
  // had ever made a draft never saw anyone else's work — the whole point of
  // storing them on the server.
  const mergedSeed = useRef(false);
  useEffect(() => {
    if (!seed || mergedSeed.current) return;
    mergedSeed.current = true;
    const fromServer = seed.map(withDerived);
    const mine = issuesRef.current;
    const next = mine ? mergeIssues(mine, fromServer) : fromServer;
    issuesRef.current = next;
    writeStored(next);
    setIssues(next);
  }, [seed]);

  useEffect(() => {
    if (issues === null) return;
    writeStored(issues);
  }, [issues]);

  const patch = useCallback((id: string, updater: (issue: IssueDetail) => IssueDetail) => {
    const cur = issuesRef.current;
    if (!cur) return;
    if (!cur.some((issue) => issue.id === id)) {
      // The editor can render a draft handed over in router state. If that
      // draft never reached the store, every edit here would be dropped in
      // silence — which is worse than a loud failure.
      if (import.meta.env.DEV) console.warn(`[issue-store] no issue ${id}; edit dropped`);
      return;
    }
    commit(cur.map((issue) => (issue.id === id ? withDerived(updater(issue)) : issue)));
  }, [commit]);

  /** Put an issue into the store, replacing any existing one with that id. */
  const upsert = useCallback((issue: IssueDetail) => {
    const cur = issuesRef.current ?? [];
    if (cur.some((x) => x.id === issue.id)) return;
    commit([withDerived(issue), ...cur]);
  }, [commit]);

  const create = useCallback((input: { title: string; subject: string; notes?: string }) => {
    const draft = createDraft(input);
    commit([draft, ...(issuesRef.current ?? [])]);
    return draft;
  }, [commit]);

  const updateFields = useCallback((id: string, fields: IssueEditable) => {
    patch(id, (issue) => ({
      ...issue,
      ...fields,
      updated_at: new Date().toISOString(),
    }));
  }, [patch]);

  const attach = useCallback((id: string, article: IssueArticle) => {
    patch(id, (issue) => {
      const list = issue.articles_list ?? [];
      if (list.some((a) => a.url === article.url)) return issue;
      const blocks = issue.blocks ?? [];
      return {
        ...issue,
        articles_list: [...list, article],
        blocks: [...blocks, { id: newId(), type: "article", text: article.title, url: article.url, alt: article.category }],
        updated_at: new Date().toISOString(),
      };
    });
  }, [patch]);

  const detach = useCallback((id: string, url: string) => {
    patch(id, (issue) => ({
      ...issue,
      articles_list: (issue.articles_list ?? []).filter((a) => a.url !== url),
      updated_at: new Date().toISOString(),
    }));
  }, [patch]);

  const submitForReview = useCallback((id: string) => {
    patch(id, (issue) => {
      if (!canSubmit(issue)) return issue;
      const next: IssueDetail = {
        ...issue,
        status: "in_review",
        reviewer: "Editorial",
        notes: issue.notes || "Submitted for editorial review.",
        updated_at: new Date().toISOString(),
      };
      return { ...next, versions: [...(issue.versions ?? []), snapshotVersion({ ...next, versions: issue.versions }, CURRENT_AUTHOR)] };
    });
  }, [patch]);

  const approve = useCallback((id: string) => {
    patch(id, (issue) => {
      const next: IssueDetail = {
        ...issue,
        status: "approved",
        reviewer: CURRENT_AUTHOR,
        notes: issue.notes || "Approved — ready to schedule.",
        updated_at: new Date().toISOString(),
      };
      return { ...next, versions: [...(issue.versions ?? []), snapshotVersion({ ...next, versions: issue.versions }, CURRENT_AUTHOR)] };
    });
  }, [patch]);

  const requestChanges = useCallback((id: string, comment: string, blockId?: string) => {
    const trimmed = comment.trim();
    patch(id, (issue) => {
      const comments: EditorComment[] = [...(issue.comments ?? [])];
      if (trimmed) {
        comments.unshift({
          id: newId(),
          block_id: blockId,
          text: trimmed,
          author: CURRENT_AUTHOR,
          at: new Date().toISOString(),
          resolved: false,
        });
      }
      const next: IssueDetail = {
        ...issue,
        status: "draft",
        comments,
        notes: trimmed
          ? `Changes requested: ${trimmed}${issue.notes ? `\n\n${issue.notes}` : ""}`
          : issue.notes,
        updated_at: new Date().toISOString(),
      };
      return { ...next, versions: [...(issue.versions ?? []), snapshotVersion({ ...next, versions: issue.versions }, CURRENT_AUTHOR)] };
    });
  }, [patch]);

  const resolveComment = useCallback((id: string, commentId: string) => {
    patch(id, (issue) => ({
      ...issue,
      comments: (issue.comments ?? []).map((c) => (c.id === commentId ? { ...c, resolved: true } : c)),
      updated_at: new Date().toISOString(),
    }));
  }, [patch]);

  const restoreVersion = useCallback((id: string, versionId: string) => {
    patch(id, (issue) => {
      const version = (issue.versions ?? []).find((v) => v.id === versionId);
      if (!version) return issue;
      return {
        ...issue,
        title: version.title,
        subject: version.subject,
        blocks: JSON.parse(JSON.stringify(version.blocks)) as ContentBlock[],
        sections: JSON.parse(JSON.stringify(version.sections ?? [])) as NewsletterSection[],
        categories: [...(version.categories ?? [])],
        notes: version.notes,
        preheader: version.preheader,
        utm: version.utm,
        updated_at: new Date().toISOString(),
      };
    });
  }, [patch]);

  return {
    issues,
    ready: issues !== null,
    /** Whether the server has caught up with what is on screen. */
    sync,
    upsert,
    create,
    updateFields,
    attach,
    detach,
    submitForReview,
    approve,
    requestChanges,
    resolveComment,
    restoreVersion,
  };
}
